import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import Portal from '../Portal/Portal';
import dropdownIcon from '../../assets/icon-arrow-down.svg';

const Wrapper = styled.div`
  position: relative;
`;

const BackgroundDiv = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
`;

const InputWrapper = styled.button<{ searching: boolean }>`
  width: 280px;
  height: 34px;
  border-radius: 2px;
  border: solid 1px rgba(36, 45, 65, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 10px;
  background-color: #fff;
  img {
    width: 12px;
    ${({ searching }): boolean =>
      searching &&
      css`
        transform: rotate(180deg);
      `}
  }
`;

const StyledInput = styled.input`
  border: none;
  padding: 0;
  width: 100%;
  height: 100%;
  outline: none;
`;

const OptionsWrapper = styled.div<{ placement?: string }>`
  position: absolute;
  top: ${({ placement }): string => (placement === 'top' ? '44px' : 'unset')};
  bottom: ${({ placement }): string => (placement === 'bottom' ? '44px' : 'unset')};
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background-color: #fff;
  width: 100%;
  border-radius: 2px;
  border: solid 1px rgba(36, 45, 65, 0.1);
`;

const StyledOption = styled.button`
  width: 100%;
  height: 32px;
  border: 1px solid #fff;
  &:hover {
    border-radius: 2px;
    border: 1px solid rgba(18, 111, 214, 0.2);
  }
`;

interface SearchDropdownProps<T extends Record<string, unknown>> {
  options: T[];
  handleSelect: (input: T[keyof T] | null) => void;
  handleSearch: (input: string) => void;
  valueKey?: keyof T;
  textKey?: keyof T;
  placement?: string;
  placeholder?: string;
}

const SearchDropdown = <T extends Record<string, unknown>>({
  options,
  handleSelect,
  handleSearch,
  valueKey,
  textKey,
  placement,
  placeholder,
}: SearchDropdownProps<T>): JSX.Element => {
  const [searching, setSearching] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchableOptions, setSearchableOptions] = useState<T[]>([]);

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchValue(event.target.value);
    handleSearch(event.target.value);
    handleSelect(null);
    setSearching(true);
  };

  const displayOptions = searchableOptions.map((option, index) => {
    const optionKey = valueKey && option[valueKey] ? String(option[valueKey]) : `option-${index}`;
    return (
      <StyledOption
        key={optionKey}
        type="button"
        onClick={(): void => {
          if (valueKey) {
            handleSelect(option[valueKey]);
          }
          if (textKey) {
            setSearchValue(String(option[textKey]));
          }
          setSearching(false);
        }}
      >
        {textKey ? String(option[textKey]) : ''}
      </StyledOption>
    );
  });

  useEffect(() => {
    if (options) {
      if (searchValue && textKey) {
        const newOptions = options.filter(
          (option) => option[textKey] && String(option[textKey]).includes(searchValue),
        );

        setSearchableOptions(newOptions);
      } else {
        setSearchableOptions(options);
      }
    }
  }, [options, searchValue, textKey]);

  return (
    <div>
      {searching && displayOptions.length > 0 ? (
        <Portal>
          <BackgroundDiv onClick={(): void => setSearching(false)} />
        </Portal>
      ) : null}
      <Wrapper>
        <InputWrapper searching={searching} onClick={(): void => setSearching(!searching)}>
          <StyledInput type="text" value={searchValue} placeholder={placeholder} onChange={handleValueChange} />
          <img src={dropdownIcon} alt="dropdown icon" />
        </InputWrapper>
        {searching && displayOptions.length > 0 ? (
          <OptionsWrapper placement={placement}>{displayOptions}</OptionsWrapper>
        ) : null}
      </Wrapper>
    </div>
  );
};

export default SearchDropdown;
